import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  Stack,
} from "@mui/material";
import moment from "moment";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import AddWorkoutDetails from "../AddWorkoutDetails";

const WorkoutImport = () => {
  const { getClients, addWorkoutForUser, importWorkouts } = useApi();
  const { isCoach, loading: userInfoLoading } = useUserInfo();
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [rawText, setRawText] = useState("");
  const [defaultWeightUnit, setDefaultWeightUnit] = useState("lbs");
  const [parsedWorkouts, setParsedWorkouts] = useState([]);
  const [selectedWorkoutIdx, setSelectedWorkoutIdx] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [savedDates, setSavedDates] = useState({});

  useEffect(() => {
    const loadClients = async () => {
      try {
        const res = await getClients();
        const active = res.filter((c) => !c.deleted);
        setClients(active);
        if (active.length) {
          setSelectedClientId(active[0].id);
        }
      } catch (err) {
        console.error(err);
        setError("Unable to load clients.");
      }
    };
    loadClients();
  }, [getClients]);

  const handleFileUpload = (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setRawText(e.target.result);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setError("");
    setStatusMessage("");
    setParsedWorkouts([]);
    setSelectedWorkoutIdx(null);
    if (!rawText.trim()) {
      setError("Paste or upload workout text before importing.");
      return;
    }
    try {
      setImporting(true);
      const resp = await importWorkouts({
        rawText,
        defaultWeightUnit,
      });
      const workouts = resp?.workouts || [];
      if (!workouts.length) {
        setError("No workouts found in the provided text.");
        return;
      }
      setParsedWorkouts(
        workouts.map((w) => ({
          clientTempId: w.clientTempId,
          order: w.order,
          warnings: w.warnings || [],
          workout: {
            ...(w.workout || {}),
            exercises: w.workout?.exercises || [],
          },
        }))
      );
      setSelectedWorkoutIdx(0);
    } catch (err) {
      console.error(err);
      setError("Failed to import workouts. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  const handleSaveSingle = async (payload) => {
    if (!selectedClientId) {
      alert("Select a client before saving.");
      return;
    }
    if (!payload?.date) {
      alert("Please pick a date before saving.");
      return;
    }
    setSaving(true);
    try {
      await addWorkoutForUser(selectedClientId, payload);
      const savedDate = payload.date;
      // persist edits into local draft
      setParsedWorkouts((prev) =>
        prev.map((item, idx) =>
          idx === selectedWorkoutIdx
            ? {
                ...item,
                workout: {
                  ...payload,
                },
              }
            : item
        )
      );
      setSavedDates((prev) => ({
        ...prev,
        [selectedWorkoutIdx]: savedDate,
      }));
      setStatusMessage(
        `Workout saved to ${moment(savedDate).format("MMM D, YYYY")}`
      );
      setTimeout(() => setStatusMessage(""), 3000);
    } catch (err) {
      console.error(err);
      alert("Failed to save workout. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (userInfoLoading) {
    return (
      <Container sx={{ mt: 8 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!isCoach) {
    return (
      <Container sx={{ mt: 8 }}>
        <Alert severity="warning">
          This page is only available to coaches.
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 6, mb: 8 }}>
      <Typography variant="h4" gutterBottom>
        Import Workouts
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Paste or upload workout text and we&apos;ll format it. Pick a client,
        preview each imported workout, set a date, and save.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="client-select-label">Client</InputLabel>
              <Select
                labelId="client-select-label"
                label="Client"
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name} — {client.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Select
              label="Default Weight Unit"
              fullWidth
              size="small"
              value={defaultWeightUnit}
              onChange={(e) => setDefaultWeightUnit(e.target.value)}
            >
              <MenuItem value="lbs">Pounds (lbs)</MenuItem>
              <MenuItem value="kg">Kilograms (kg)</MenuItem>
              <MenuItem value="bodyweight">Bodyweight</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              component="label"
              variant="outlined"
              fullWidth
              sx={{ mt: { xs: 2, md: 0 } }}
            >
              Upload .txt
              <input
                type="file"
                hidden
                accept=".txt,.md,text/plain"
                onChange={handleFileUpload}
              />
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={importing}
              fullWidth
            >
              {importing ? "Importing..." : "Import"}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Paste workout text"
              placeholder="Paste the workout text here..."
              multiline
              minRows={10}
              fullWidth
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {statusMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {statusMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Imported Workouts
        </Typography>
        {!parsedWorkouts.length ? (
          <Typography color="text.secondary">
            No workouts parsed yet. Paste or upload text, then click Import.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                {parsedWorkouts.map((item, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 2,
                      border:
                        selectedWorkoutIdx === idx
                          ? "2px solid #ff0000"
                          : "1px solid #e0e0e0",
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectedWorkoutIdx(idx)}
                  >
                    <Typography variant="subtitle1" noWrap>
                      {item.workout?.name || "Untitled Workout"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {savedDates[idx]
                        ? `Saved ${moment(savedDates[idx]).format("MMM D")}`
                        : moment().format("dddd, MMM D")}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {item.workout?.exercises?.length || 0} exercises
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={8}>
              {selectedWorkoutIdx !== null ? (
                <AddWorkoutDetails
                  mode="workout"
                  userId={selectedClientId}
                  workout={{
                    ...(parsedWorkouts[selectedWorkoutIdx]?.workout || {}),
                    date:
                      parsedWorkouts[selectedWorkoutIdx]?.workout?.date != null
                        ? moment(
                            parsedWorkouts[selectedWorkoutIdx].workout.date
                          )
                        : moment(),
                  }}
                  onSave={handleSaveSingle}
                  handleClose={() => {}}
                  draftTemplateMode
                  submitButtonId="import-workout-save-button"
                  onDeleteDraft={() => {
                    setParsedWorkouts((prev) =>
                      prev.filter((_, i) => i !== selectedWorkoutIdx)
                    );
                    setSelectedWorkoutIdx((prevIdx) => {
                      const nextLength = parsedWorkouts.length - 1;
                      if (nextLength <= 0) return null;
                      if (prevIdx === null) return null;
                      if (prevIdx >= nextLength) return nextLength - 1;
                      return prevIdx;
                    });
                  }}
                />
              ) : (
                <Typography color="text.secondary">
                  Select a workout to edit and save.
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default WorkoutImport;
